import styled from "@emotion/styled";
import { kebabCase } from "change-case";
import { useField, useFormikContext } from "formik";
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type MouseEvent,
  type Ref,
} from "react";

import type { TextField } from "@skilitics/designer-ui-components";
import {
  AdornmentIcon,
  Button,
  FONT_WEIGHT_NORMAL,
  IconButton,
  InfoTooltip,
  PropertiesPanelPopover,
  SkiliticsIcon,
  Text,
  Tooltip,
  toSpaceCase,
  useDebouncedFn,
  useFieldContext,
  useHandleBlur,
  type TestAttribute,
} from "@skilitics/designer-ui-components";
import { incrementCssValue } from "@skilitics/drag-drop";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useCssBindings } from "../bindings";

const SettingsMenuButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.1s ease-in-out;
  height: 22px;
  width: 22px;
  font-size: 12px;
  background-color: ${({ theme }) => theme.surface};

  &:hover {
    // note: colors are effective colors from MUI hover on light and dark themes
    background-color: ${({ theme }) => theme.surfaceVariant};
  }
`;

const StyledTextField = styled(Text)`
  width: 100%;
  input {
    text-align: left;
    padding: 0;
  }
  ${AdornmentIcon} {
    margin: 0 6px 0 0;
  }
  &:hover ${SettingsMenuButton} {
    opacity: 1;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  padding-top: 15px;
`;

const BindingButton = styled(Button)`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  min-width: 0;
  line-height: 18px;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.secondaryContainer};
  color: ${({ theme }) => theme.onSecondaryContainer};
  font-weight: ${FONT_WEIGHT_NORMAL};
  transition: ease-in 0.2s all;

  &:hover {
    background-color: ${({ theme }) => theme.secondary};
    color: ${({ theme }) => theme.onSecondary};
  }
`;

export type FieldCssInputProps = Omit<CssInputProps, "value" | "onChange"> & {
  onBlur?: FocusEventHandler<HTMLInputElement>;
  sizeFormatSuffix?: string;
  inactive?: boolean;
  tooltip?: string;
  /**
   * CSS property name for validation. When provided, validates that the input value
   * is a valid CSS value for this property using CSS.supports().
   * If not provided, will use the form field name as the CSS property.
   */
  propertyName?: string;
  /** CSS unit to append during form submission (default: "px") */
  unit?: string;

  // TODO: This could just be `initial` as a valid CSS "default empty value"? Unsure of need here.
  /**
   * Default value to use for CSS validation when input is empty (default: "0").
   * It's important to check the default empty value is valid, for example:
   * rotate: 0; is invalid, because rotate: none; is the default empty value.
   */
  defaultEmptyValue?: string;
  /** Override error messages for specific CSS properties */
  formatStringOverrides?: Partial<CssValidationFormatStrings>;
  /** Arrow-key increment step (default: 1). Shift+Arrow uses 10× this. */
  step?: number;
};

export const FieldCssInput = ({
  onBlur,
  onFocus,
  sizeFormatSuffix,
  type,
  label,
  slotProps,
  inactive,
  placeholder,
  ref,
  propertyName,
  unit = "px",
  defaultEmptyValue = "0",
  formatStringOverrides,
  tooltip,
  step = 1,
  ...rest
}: FieldCssInputProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [focussed, setFocussed] = useState(false);
  const { onAddBinding, bindings } = useCssBindings();
  const { controlId, dataTest, name } = useFieldContext();
  const formatStrings = useMemo(
    () => ({ ...cssValidationFormatStrings, ...formatStringOverrides }),
    [formatStringOverrides],
  );
  const formatMessage = useFormatMessage(formatStrings);
  const inputRef = useRef<HTMLInputElement>(null);
  const cssProperty = propertyName || name;
  const { submitForm } = useFormikContext();
  const debouncedSubmit = useDebouncedFn(submitForm, 500);

  const validate = useCallback(
    (inputValue: string) => {
      // TODO: implement as a warning indicator instead of an error.
      // CSS Vars can be defined outside of bindings so we should still allow them to be saved.
      // Check for CSS variable bindings (--something)
      // const variableRegex = /--[\w-]+/g;
      // const variableMatches = value.match(variableRegex);
      // if (variableMatches) {
      //   for (const match of variableMatches) {
      //     if (!doesBindingExist(match)) {
      //       return `No binding found for ${match}`;
      //     }
      //   }
      // }

      // Check CSS property validity if propertyName is provided or can be derived from field name
      if (cssProperty) {
        const property = kebabCase(cssProperty);
        const val = inputValue ? inputValue : defaultEmptyValue;

        if (!!val && !CSS.supports(property, val)) {
          // Use the property-specific message when one exists (checking the merged
          // strings so formatStringOverrides can supply it), otherwise fall back to
          // a generic message — never throw just because a message wasn't registered.
          const messageKey = property in formatStrings ? (property as keyof typeof formatStrings) : "invalid";
          return formatMessage(messageKey);
        }
      }
    },
    [cssProperty, defaultEmptyValue, formatStrings, formatMessage],
  );
  const [{ value, onChange }, { error }, { setError, setValue }] = useField<string>({ name, validate });

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      // Run validation immediately on change if there is an existing error.
      if (error) {
        const validationError = validate(newValue);
        if (validationError && validationError !== error) {
          setError(validationError);
        }
        if (validationError === undefined) {
          setError(undefined);
        }
      }

      onChange(event);
    },
    [onChange, validate, error, setError],
  );

  const onBlurHook = useHandleBlur();

  const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      setFocussed(false);
      let valid = validate(value);
      const valueOrDefault = value ? value : defaultEmptyValue;

      // Handle unit appending if there's a validation error and CSS property can be determined
      // Ignore if the value ends in a non-numeric character (e.g. %, em, vw, or ) closing bracket)
      if (valid !== undefined && !isNaN(Number(valueOrDefault.slice(-1)))) {
        const appendedValue = valueOrDefault + unit;

        valid = validate(appendedValue);
        if (valid !== undefined) return;

        setError(undefined);
        if (value) {
          setValue(appendedValue)
            .then(() => onBlurHook(event))
            .catch(console.warn);
        }
      } else {
        setError(valid);
        onBlurHook(event);
      }

      onBlur?.(event);
    },
    [validate, value, onBlur, onBlurHook, defaultEmptyValue, unit, setValue, setError],
  );

  const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      // Select all text when input is focused
      event.target.select();

      onFocus?.(event);
      setFocussed(true);
    },
    [onFocus],
  );

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

      const stepAmount = event.shiftKey ? step * 10 : step;
      const delta = event.key === "ArrowUp" ? stepAmount : -stepAmount;
      const newValue = incrementCssValue({ value, delta, fallbackUnit: unit });
      if (newValue === undefined) return;

      event.preventDefault();
      event.stopPropagation();
      void setValue(newValue).then(() => debouncedSubmit());
    },
    [value, unit, step, setValue, debouncedSubmit],
  );

  const handleOpenSettings = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleBindingClick = (binding: (typeof bindings)[number]) => (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    void setValue(`calc(var(${binding.config.name}) * 1${unit})`);
    inputRef.current?.focus();
  };

  const displayValue = useMemo(() => {
    if (inactive && !focussed) {
      return "";
    }
    return value ? (sizeFormatSuffix && !focussed ? `${value}${sizeFormatSuffix}` : value) : "";
  }, [focussed, inactive, sizeFormatSuffix, value]);

  return (
    <>
      <Tooltip title={tooltip} placement="top" enterDelay={1000}>
        <CssInput
          {...rest}
          ref={ref}
          id={controlId}
          name={name}
          value={displayValue}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={inactive && value ? value : placeholder}
          error={!!error}
          slotProps={{
            ...slotProps,
            htmlInput: {
              ...slotProps?.htmlInput,
              "data-test": `${dataTest ?? name}-input`,
              inputMode: type === "number" ? "numeric" : undefined,
              pattern: type === "number" ? "-?[0-9]*" : undefined,
            },
            input: {
              ...slotProps?.input,
              startAdornment: label ? <AdornmentIcon>{label}</AdornmentIcon> : undefined,
              endAdornment: !rest.disabled && (
                <SettingsMenuButton data-test={`${name}-settings`} onClick={handleOpenSettings} tabIndex={-1}>
                  <SkiliticsIcon icon="advanced-settings" />
                </SettingsMenuButton>
              ),
            },
          }}
        />
      </Tooltip>

      {anchorEl && (
        <PropertiesPanelPopover
          id={`${name}-settings-popover`}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          header={
            <span>
              {toSpaceCase(name)}{" "}
              <InfoTooltip
                tooltip={`Write any valid CSS for the ${cssProperty} property. Add bindings so they can be referenced in CSS with var(--bindingName)`}
                tooltipPlacement="top"
              />
            </span>
          }
        >
          <CssInput
            {...rest}
            ref={ref}
            id={controlId}
            name={name}
            value={displayValue}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={inactive && value ? value : placeholder}
            error={!!error}
            slotProps={{
              htmlInput: {
                ref: inputRef,
              },
            }}
          />

          <ButtonContainer>
            {bindings.map((b) => (
              <Tooltip key={b.id} title={b.config.name} placement="top">
                <BindingButton variant="text" size="small" onClick={handleBindingClick(b)}>
                  {b.config.name}
                </BindingButton>
              </Tooltip>
            ))}
            <Button variant="outlined" size="small" onClick={() => onAddBinding(anchorEl)}>
              <SkiliticsIcon icon="add" /> Add Binding
            </Button>
          </ButtonContainer>
        </PropertiesPanelPopover>
      )}
    </>
  );
};

export type CssInputProps = Omit<ComponentProps<typeof TextField>, "ref"> &
  TestAttribute & {
    value: string;
    name?: string;
    type?: "number" | "text";
    ref?: Ref<HTMLInputElement>;
  };

export const CssInput = ({ name, ref, ...props }: CssInputProps) => {
  return <StyledTextField {...props} ref={ref} name={name} variant="standard" />;
};

type CssValidationFormatStrings = typeof cssValidationFormatStrings;

const cssValidationFormatStrings = {
  width: {
    en: "Invalid width",
  },
  height: {
    en: "Invalid height",
  },
  "min-width": {
    en: "Invalid minimum width",
  },
  "min-height": {
    en: "Invalid minimum height",
  },
  "max-width": {
    en: "Invalid maximum width",
  },
  "max-height": {
    en: "Invalid maximum height",
  },
  "padding-inline": {
    en: "Invalid inline padding length",
  },
  "padding-block": {
    en: "Invalid block padding length",
  },
  "padding-left": {
    en: "Invalid left padding length",
  },
  "padding-right": {
    en: "Invalid right padding length",
  },
  "padding-top": {
    en: "Invalid top padding length",
  },
  "padding-bottom": {
    en: "Invalid bottom padding length",
  },
  "row-gap": {
    en: "Invalid row gap length",
  },
  "column-gap": {
    en: "Invalid column gap length",
  },
  "border-radius": {
    en: "Invalid border radius",
  },
  border: {
    en: "Invalid outline width",
  },
  "border-width": {
    en: "Invalid outline width",
  },
  "border-top": {
    en: "Invalid top outline width",
  },
  "border-bottom": {
    en: "Invalid bottom outline width",
  },
  "border-left": {
    en: "Invalid left outline width",
  },
  "border-right": {
    en: "Invalid right outline width",
  },
  left: {
    en: "Invalid left position",
  },
  top: {
    en: "Invalid top position",
  },
  right: {
    en: "Invalid right position",
  },
  bottom: {
    en: "Invalid bottom position",
  },
  rotate: {
    en: "Invalid rotation",
  },
  opacity: {
    en: "Invalid opacity",
  },
  "transform-origin": {
    en: "Invalid transform origin",
  },
  "z-index": {
    en: "Invalid Z Index",
  },
  // Generic fallback for any property without a specific message above.
  invalid: {
    en: "Invalid value",
  },
} satisfies FormatMessages;
